import React, { useId, useMemo, useState } from "react"

import {
  useAnnotationsWithMetricOutputsTransport,
  useReviewMetricTransportInvalidation,
  useUpdateReviewAnnotationTransport,
} from "@/human-feedback/transport"
import { MetricVersionTag } from "@/metrics/metric-display"
import { Switch, toast } from "@coval/ui"
import { Skeleton } from "@coval/ui/skeleton"
import { Tooltip } from "@coval/ui/tooltip"
import { AlertTriangle, Copy, Star } from "lucide-react"

import { useActiveItemAutoScroll } from "@/hooks/useActiveItemAutoScroll"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"
import { handleAccessibleRowKeyDown } from "@/utils/accessibleRowKeyDown"
import { cn } from "@/utils/cn"

import {
  DEFAULT_SIMULATION_FILTERS,
  filterAndSortSimulations,
  groupAnnotationsBySimulation,
  type GroupedSimulationData,
  type HumanReviewData,
  type SimulationFilters,
} from "../../utils/filterSimulations"
import { useTestDrawer, useTestMetricRuntime } from "../contexts"
import type { ExperimentTestResult } from "../types"
import {
  DEFAULT_TOLERANCE_SECONDS,
  getHumanSegments,
  getMetricSegments,
  segmentsAgree,
  segmentsAgreeWithTolerance,
  type Segment,
} from "../utils/segmentAgreement"
import { DualSegmentTimeline } from "./DualSegmentTimeline"

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------

interface AudioMetricsRowProps {
  simulationOutputId: string
  durationSeconds: number | undefined
  metricSegments: Segment[]
  humanSegments: Segment[]
  testSegments?: Segment[]
  hasConflict: boolean
  isActive: boolean
  metricUnavailable: boolean
  isEvaluating: boolean
  onRowClick: (simulationOutputId: string) => void
  onPointerEnter: (simulationOutputId: string) => void
  onPointerLeave: (simulationOutputId: string) => void
  rawAnnotations: GroupedSimulationData["rawAnnotations"]
  humanReviews: HumanReviewData[]
  isExpanded: boolean
  onToggleExpand: () => void
  onSetPrimary: (reviewId: string) => void
  isSettingPrimary: boolean
  versionStatus: {
    ranVersion: number
    currentVersion: number
    isStale: boolean
  } | null
}

const AudioMetricsRow: React.FC<AudioMetricsRowProps> = ({
  simulationOutputId,
  durationSeconds,
  metricSegments,
  humanSegments,
  testSegments,
  hasConflict,
  isActive,
  metricUnavailable,
  isEvaluating,
  onRowClick,
  onPointerEnter,
  onPointerLeave,
  rawAnnotations,
  humanReviews,
  isExpanded,
  onToggleExpand,
  onSetPrimary,
  isSettingPrimary,
  versionStatus,
}) => {
  const handleCopyId = () => {
    void navigator.clipboard.writeText(simulationOutputId)
  }

  const versionBadge = (
    <MetricVersionTag
      precomputedStatus={versionStatus}
      variant="full"
      className="ml-14"
    />
  )

  const primaryReview =
    humanReviews.find((r) => r.isCountedAsPrimary) || humanReviews[0]
  const additionalCount = humanReviews.length - 1

  // Check if reviewers disagree on segments
  const hasReviewerDisagreement = useMemo(() => {
    if (rawAnnotations.length <= 1) {
      return false
    }
    const primaryAnnotation =
      rawAnnotations.find((a) => a.priority === "PRIMARY") || rawAnnotations[0]
    if (!primaryAnnotation) {
      return false
    }
    const primarySegs = getHumanSegments(primaryAnnotation)
    return rawAnnotations.some((ann) => {
      if (ann.id === primaryAnnotation.id) {
        return false
      }
      const otherSegs = getHumanSegments(ann)
      return !segmentsAgree(primarySegs, otherSegs)
    })
  }, [rawAnnotations])

  return (
    <tr
      className={cn(
        "border-b border-coval-border cursor-pointer transition-colors hover:bg-surface-1/50",
        isActive && !hasConflict && "bg-surface-1/50",
      )}
      data-audio-simulation-review-id={simulationOutputId}
      style={
        hasConflict ? { backgroundColor: "var(--coval-danger-surface)" } : {}
      }
      tabIndex={0}
      onClick={() => onRowClick(simulationOutputId)}
      onKeyDown={(event) =>
        handleAccessibleRowKeyDown(event, () => onRowClick(simulationOutputId))
      }
      onMouseEnter={() => onPointerEnter(simulationOutputId)}
      onMouseLeave={() => onPointerLeave(simulationOutputId)}
    >
      <td
        className="px-4 py-4 text-left w-[80px]"
        style={{ verticalAlign: "top" }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-coval-text-muted font-mono">
            {simulationOutputId.slice(0, 5)}
          </span>
          <Tooltip content="Copy ID">
            <button
              type="button"
              aria-label="Copy simulation ID"
              onClick={(e) => {
                e.stopPropagation()
                handleCopyId()
              }}
              className="text-coval-text-muted hover:text-coval-text-muted transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>
      </td>
      <td
        className="px-4 py-4 text-left align-top w-2/3"
        style={{ verticalAlign: "top" }}
      >
        {!isExpanded ? (
          <div className="flex flex-col gap-2">
            <DualSegmentTimeline
              durationSeconds={durationSeconds}
              metricSegments={metricSegments}
              humanSegments={humanSegments}
              testSegments={testSegments}
              metricUnavailable={metricUnavailable}
              isEvaluating={isEvaluating}
              rowHeight={12}
              className="min-w-[160px]"
              middleContent={versionBadge}
            />
            {humanReviews.length > 0 && (
              <div className="flex items-center gap-2">
                {(primaryReview?.reviewer || primaryReview?.date) && (
                  <span className="text-xs text-coval-text-muted">
                    {[primaryReview?.reviewer, primaryReview?.date]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                )}
                {additionalCount > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onToggleExpand()
                    }}
                    className="text-xs text-coval-text-muted underline hover:text-coval-text transition-colors"
                  >
                    +{additionalCount} more
                  </button>
                )}
                {hasReviewerDisagreement && (
                  <Tooltip content="Reviewers disagree">
                    <AlertTriangle className="w-3.5 h-3.5 text-coval-danger-text" />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {rawAnnotations.map((ann) => {
              const review = humanReviews.find((r) => r.id === ann.id)
              const annHumanSegs = getHumanSegments(ann)
              return (
                <div key={ann.id} className="flex flex-col gap-1 group">
                  <DualSegmentTimeline
                    durationSeconds={durationSeconds}
                    metricSegments={metricSegments}
                    humanSegments={annHumanSegs}
                    testSegments={testSegments}
                    metricUnavailable={metricUnavailable}
                    isEvaluating={isEvaluating}
                    rowHeight={12}
                    className="min-w-[160px]"
                    middleContent={versionBadge}
                  />
                  <div className="flex items-center gap-2">
                    {(review?.reviewer || review?.date) && (
                      <span className="text-xs text-coval-text-muted">
                        {[review?.reviewer, review?.date]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    )}
                    {humanReviews.length > 1 && (
                      <Tooltip
                        content={
                          review?.isCountedAsPrimary
                            ? "Counted in stats"
                            : "Set as primary"
                        }
                      >
                        <button
                          type="button"
                          aria-label={
                            review?.isCountedAsPrimary
                              ? "Currently counted in stats"
                              : "Set as primary review"
                          }
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!review?.isCountedAsPrimary && review) {
                              onSetPrimary(review.id)
                            }
                          }}
                          disabled={
                            review?.isCountedAsPrimary || isSettingPrimary
                          }
                          className={`transition-all ${
                            review?.isCountedAsPrimary
                              ? "text-coval-warning-text"
                              : "text-coval-text-muted opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 hover:text-coval-warning-text"
                          } ${isSettingPrimary ? "opacity-50" : ""}`}
                        >
                          <Star
                            className="w-3 h-3"
                            fill={
                              review?.isCountedAsPrimary
                                ? "currentColor"
                                : "none"
                            }
                          />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )
            })}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand()
              }}
              className="text-xs text-coval-text-muted underline hover:text-coval-text transition-colors text-left"
            >
              Show less
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

type Filters = SimulationFilters

interface AudioMetricsSimulationReviewTableProps {
  filters?: Filters
  humanReviewProjects?: HumanReviewProject[]
  experimentResults?: Record<string, ExperimentTestResult>
  evaluatingIds?: Set<string>
}

export const AudioMetricsSimulationReviewTable: React.FC<
  AudioMetricsSimulationReviewTableProps
> = ({
  filters = DEFAULT_SIMULATION_FILTERS,
  humanReviewProjects,
  experimentResults = {},
  evaluatingIds = new Set(),
}) => {
  const { metric, includeOldVersions, setIncludeOldVersions } =
    useTestMetricRuntime()
  const versionToggleId = useId()
  const usesRegions = metric?.getAudioRegionReviewConfig()?.useRegions === true
  const { handleRowClick } = useTestDrawer()
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null)

  const invalidateReviewMetricTransport = useReviewMetricTransportInvalidation()

  const updateAnnotationMutation = useUpdateReviewAnnotationTransport({
    onSuccess: () => {
      invalidateReviewMetricTransport()
    },
  })

  const handleSetPrimary = async (reviewId: string, allReviewIds: string[]) => {
    setSettingPrimaryId(reviewId)
    const otherIds = allReviewIds.filter((id) => id !== reviewId)
    try {
      await Promise.all(
        otherIds.map((otherId) =>
          updateAnnotationMutation.mutateAsync({
            annotationId: otherId,
            priority: "STANDARD",
          }),
        ),
      )
      await updateAnnotationMutation.mutateAsync({
        annotationId: reviewId,
        priority: "PRIMARY",
      })
    } catch {
      toast.danger({
        title: "Failed to update primary review",
        duration: 6000,
      })
      invalidateReviewMetricTransport()
    } finally {
      setSettingPrimaryId(null)
    }
  }

  const handleToggleExpand = (simulationOutputId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(simulationOutputId)) {
        newSet.delete(simulationOutputId)
      } else {
        newSet.add(simulationOutputId)
      }
      return newSet
    })
  }

  const {
    data: annotationsWithMetricOutputs,
    isLoading,
    isError,
    error,
  } = useAnnotationsWithMetricOutputsTransport({ metricId: metric?.id })

  const baseGroupedData = useMemo(
    () =>
      groupAnnotationsBySimulation(
        annotationsWithMetricOutputs,
        metric?.id,
        metric?.output_type || "string",
        filters.includeUnreviewed,
      ),
    [
      annotationsWithMetricOutputs,
      metric?.id,
      metric?.output_type,
      filters.includeUnreviewed,
    ],
  )

  const filteredAndSortedData = useMemo(() => {
    // Pre-compute segments once per item so filtering and rendering share the
    // same source of truth.
    const withSegments = baseGroupedData.map((item) => {
      const fullMetricOutput =
        annotationsWithMetricOutputs?.metricOutputs[metric?.id || ""]?.[
          item.simulationOutputId
        ] ?? null
      const primaryAnnotation =
        item.rawAnnotations.find((a) => a.priority === "PRIMARY") ??
        item.rawAnnotations[0] ??
        null
      const metricSegs = getMetricSegments(metric, fullMetricOutput)
      const humanSegs = getHumanSegments(primaryAnnotation)
      const hasSegmentReview =
        primaryAnnotation?.ground_truth_subvalues_by_timestamp != null ||
        (
          primaryAnnotation as {
            groundTruthSubvaluesByTimestamp?: unknown
          } | null
        )?.groundTruthSubvaluesByTimestamp != null ||
        humanSegs.length > 0
      const isStaleVersion =
        fullMetricOutput && metric
          ? metric.isMetricOutputStale(fullMetricOutput)
          : false
      const versionStatus =
        fullMetricOutput && metric
          ? metric.getOutputVersionStatus(fullMetricOutput)
          : null
      return {
        ...item,
        metricSegs,
        humanSegs,
        hasMetricOutput: fullMetricOutput != null,
        hasSegmentReview,
        isStaleVersion,
        versionStatus,
      }
    })

    // Exclude annotations that only have scalar values (no segment review)
    const segmentReviewed = withSegments.filter(
      (item) => item.hasSegmentReview || item.metricSegs.length > 0,
    )

    const baseData = segmentReviewed.map((item) => ({
      simulationOutputId: item.simulationOutputId,
      humanReviews: item.humanReviews,
      rawAnnotations: item.rawAnnotations,
      rawMetricOutput: item.rawMetricOutput,
    }))
    const metricOutputsForMetric =
      annotationsWithMetricOutputs?.metricOutputs[metric?.id || ""]
    const filtered = filterAndSortSimulations(
      baseData,
      filters,
      metric,
      metricOutputsForMetric,
      humanReviewProjects,
    )
    const filteredMap = new Map(
      filtered.map((item, index) => [item.simulationOutputId, { item, index }]),
    )
    return segmentReviewed
      .filter((item) => filteredMap.has(item.simulationOutputId))
      .map((item) => {
        const filteredEntry = filteredMap.get(item.simulationOutputId)!
        // Recompute segments from filtered primary annotation
        const filteredPrimary =
          filteredEntry.item.rawAnnotations.find(
            (a) => a.priority === "PRIMARY",
          ) ??
          filteredEntry.item.rawAnnotations[0] ??
          null
        const filteredHumanSegs = getHumanSegments(filteredPrimary)
        const filteredHasSegmentReview =
          filteredPrimary?.ground_truth_subvalues_by_timestamp != null ||
          (
            filteredPrimary as {
              groundTruthSubvaluesByTimestamp?: unknown
            } | null
          )?.groundTruthSubvaluesByTimestamp != null ||
          filteredHumanSegs.length > 0
        return {
          ...item,
          humanReviews: filteredEntry.item.humanReviews,
          rawAnnotations: filteredEntry.item.rawAnnotations,
          rawMetricOutput: filteredEntry.item.rawMetricOutput,
          humanSegs: filteredHumanSegs,
          hasSegmentReview: filteredHasSegmentReview,
        }
      })
      .sort((a, b) => {
        const indexA = filteredMap.get(a.simulationOutputId)!.index
        const indexB = filteredMap.get(b.simulationOutputId)!.index
        return indexA - indexB
      })
  }, [
    baseGroupedData,
    filters,
    metric,
    annotationsWithMetricOutputs,
    humanReviewProjects,
  ])

  const {
    handlePointerEnter,
    handlePointerLeave,
    isActiveItem,
    keyboardActiveItemId,
  } = useCollectionPageShortcuts({
    enabled: true,
    getItemId: (item) => item.simulationOutputId,
    itemLabel: "simulation",
    items: filteredAndSortedData,
    onOpenItem: (item) => handleRowClick(item.simulationOutputId),
  })

  useActiveItemAutoScroll({
    activeItemId: keyboardActiveItemId,
    selectorAttribute: "data-audio-simulation-review-id",
  })

  const versionToggle = metric?.hasVersionedConfig ? (
    <div className="flex items-center justify-end px-4 py-2 border-b border-coval-border">
      <div className="flex items-center gap-2">
        <Switch
          id={versionToggleId}
          checked={includeOldVersions}
          onCheckedChange={setIncludeOldVersions}
        />
        <label
          htmlFor={versionToggleId}
          className="text-xs text-coval-text-muted cursor-pointer"
        >
          Include old versions
        </label>
      </div>
    </div>
  ) : null

  if (isLoading) {
    return (
      <div>
        {versionToggle}
        <div className="flex items-center justify-center py-12">
          <Skeleton className="h-4 w-16 rounded-md" />
        </div>
      </div>
    )
  }

  if (
    isError ||
    !annotationsWithMetricOutputs ||
    !Array.isArray(baseGroupedData)
  ) {
    return (
      <div>
        {versionToggle}
        <div className="flex items-center justify-center py-12 text-coval-danger-text text-sm">
          {error?.message || "Failed to load annotations. Please try again."}
        </div>
      </div>
    )
  }

  if (baseGroupedData.length === 0) {
    return (
      <div>
        {versionToggle}
        <div className="flex items-center justify-center py-12 text-coval-text-muted text-sm">
          No annotations found for this metric
        </div>
      </div>
    )
  }

  if (filteredAndSortedData.length === 0) {
    return (
      <div>
        {versionToggle}
        <div className="flex items-center justify-center py-12 text-coval-text-muted text-sm">
          No results match the current filters
        </div>
      </div>
    )
  }

  return (
    <div>
      {versionToggle}
      <table className="w-full text-left table-fixed">
        <thead>
          <tr className="border-b border-coval-border">
            <th className="sticky top-0 z-10 px-4 py-3 text-sm font-medium text-coval-text w-[80px] bg-surface-default">
              Sim ID
            </th>
            <th className="sticky top-0 z-10 px-4 py-3 text-sm font-medium text-coval-text w-2/3 bg-surface-default">
              Segment comparison
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedData.map((data) => {
            const audioLength =
              annotationsWithMetricOutputs?.audioLengthsBySimulationOutputId?.[
                data.simulationOutputId
              ]
            const durationSeconds =
              typeof audioLength === "number" && audioLength > 0
                ? audioLength
                : undefined

            // Reuse segments pre-computed in the useMemo above
            const { metricSegs, humanSegs } = data
            const hasConflict =
              metricSegs.length > 0 || humanSegs.length > 0
                ? usesRegions
                  ? !segmentsAgreeWithTolerance(
                      metricSegs,
                      humanSegs,
                      filters.toleranceSeconds ?? DEFAULT_TOLERANCE_SECONDS,
                    )
                  : !segmentsAgree(metricSegs, humanSegs)
                : false

            const experimentResult = experimentResults[data.simulationOutputId]
            const testSegs = experimentResult?.rawMetricOutput
              ? getMetricSegments(metric, experimentResult.rawMetricOutput)
              : undefined

            return (
              <AudioMetricsRow
                key={data.simulationOutputId}
                simulationOutputId={data.simulationOutputId}
                durationSeconds={durationSeconds}
                metricSegments={metricSegs}
                humanSegments={humanSegs}
                testSegments={testSegs}
                hasConflict={
                  (!data.isStaleVersion || includeOldVersions) && hasConflict
                }
                isActive={isActiveItem(data.simulationOutputId)}
                metricUnavailable={!data.hasMetricOutput}
                isEvaluating={evaluatingIds.has(data.simulationOutputId)}
                onRowClick={handleRowClick}
                onPointerEnter={handlePointerEnter}
                onPointerLeave={handlePointerLeave}
                rawAnnotations={data.rawAnnotations}
                humanReviews={data.humanReviews}
                isExpanded={expandedRows.has(data.simulationOutputId)}
                onToggleExpand={() =>
                  handleToggleExpand(data.simulationOutputId)
                }
                onSetPrimary={(reviewId) => {
                  void handleSetPrimary(
                    reviewId,
                    data.humanReviews.map((r) => r.id),
                  )
                }}
                versionStatus={data.versionStatus}
                isSettingPrimary={data.humanReviews.some(
                  (r) => r.id === settingPrimaryId,
                )}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
